import axios, { AxiosError } from 'axios';

import { useAuthStore } from '@/features/auth/authStore';

/**
 * URL base de la API:
 *  - En desarrollo, VITE_API_URL=http://localhost:8000 → la SPA pega al backend en otro puerto.
 *  - En producción, VITE_API_URL queda vacía → la SPA pega al mismo dominio que la sirve
 *    (Laravel sirve los assets en /app/* y la API en /api/*).
 */
const rootBase = import.meta.env.VITE_API_URL ?? '';
const apiBase = rootBase + '/api/v1';

export const api = axios.create({
  baseURL: apiBase,
  headers: { Accept: 'application/json' },
  // Necesario para que axios envíe los cookies XSRF-TOKEN y laravel_session
  // (Sanctum stateful los usa para validar el request).
  withCredentials: true,
});

/**
 * Refresca el cookie XSRF-TOKEN antes de un request lento o multipart.
 * Sanctum stateful pone el cookie en la respuesta de /sanctum/csrf-cookie;
 * después axios automáticamente lo manda como header X-XSRF-TOKEN en los
 * siguientes POSTs.
 */
export async function refreshCsrf(): Promise<void> {
  try {
    await axios.get(rootBase + '/sanctum/csrf-cookie', {
      withCredentials: true,
    });
  } catch {
    // Silenciar — si falla, el POST que sigue va a devolver un 419 que
    // el caller debe manejar.
  }
}

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().reset();
    }
    return Promise.reject(error);
  },
);

export interface Paginated<T> {
  data: T[];
  meta: { current_page: number; per_page: number; total: number; last_page: number };
}
